/**
 * Seeding parsed DICOM objects into the wadouri loader's dataset cache.
 *
 * Bundle streaming only pays off if the bytes it delivers are the bytes the
 * viewports would otherwise have fetched one at a time. Cornerstone's wadouri
 * loader keys its dataset cache by the URL inside the imageId — so "delivering"
 * a bundled object means writing a parsed `DataSet` under exactly the key
 * `loadImage` will look up. Get the key wrong by one character and nothing
 * breaks visibly: every object is simply fetched twice, once in the bundle and
 * once per object, which is worse than not bundling at all.
 *
 * That is why the key is never composed here by hand. It comes out of
 * `createImageIdsForImage` (the single place the whole app builds imageIds) run
 * through the loader's OWN `parseImageId`, so the two can only agree. A study
 * whose clinic serves DICOM over the LAN gets LAN-shaped imageIds from
 * `imageIdSourceUrl`, and therefore seeds under LAN keys automatically, with no
 * branch here.
 */
import { cache } from '@cornerstonejs/core';
import dataSetCacheManager, { loadedDataSets, } from '@cornerstonejs/dicom-image-loader/wadouri/dataSetCacheManager';
import parseImageId from '@cornerstonejs/dicom-image-loader/wadouri/parseImageId';
import * as dicomParser from 'dicom-parser';
import { createImageIdsForImage } from './imageIds';
/**
 * The dataset-cache key for an instance: the imageId the viewports will use,
 * minus the `wadouri:` scheme and any `?frame=`/`&frame=` suffix.
 *
 * Multi-frame instances expand to one imageId per frame but share ONE dataset,
 * and `parseImageId` strips the frame suffix, so every frame of an object
 * resolves to the same key — seeding once serves all of them.
 *
 * EMPTY STRING means the instance has no address yet and therefore no key: its
 * pixels are still uploading, or it lives only on a gateway this clinic does not
 * serve from. A key is a URL, so empty is unambiguous, and it is what this
 * already produced for such a row before the server started withholding the URL
 * outright (it built `wadouri:` out of an empty string). Every caller below
 * refuses to file anything under it.
 */
export function datasetCacheKey(image) {
    const [firstImageId] = createImageIdsForImage(image);
    if (!firstImageId)
        return '';
    return parseImageId(firstImageId).url;
}
/**
 * Cache keys whose entry THIS module created.
 *
 * The cache is shared with the per-object loader, which writes the same keys
 * from its own XHRs, and the two are indistinguishable once written: an entry
 * is a `{ dataSet, cacheCount }` with nothing on it saying who put it there.
 * Retraction has to know, because unloading someone else's entry deletes a
 * dataset a viewport is reading. So ownership is recorded at the one moment it
 * is known — the write itself.
 */
const seededKeys = new Set();
/**
 * Parse `bytes` and publish the result as `image`'s cached dataset.
 *
 * Returns false when the bytes are not parseable DICOM — a zero-padded frame
 * the proxy could not fill, or a truncated stream. The caller counts that
 * ordinal as failed and lets the per-object path fetch it normally; there is no
 * code behind that fallback, an unseeded key just loads the way it always did.
 */
export function seedDataSet(image, bytes) {
    const key = datasetCacheKey(image);
    // No address, no key (see datasetCacheKey). Caching these bytes would file
    // every such instance under the same empty key, so the caller is told the seed
    // did not take and the row loads normally once its URL arrives.
    if (!key)
        return false;
    // Already cached — by an earlier bundle, or by a per-object XHR that beat us.
    // Overwriting would be harmless but pointless work; skipping also leaves the
    // existing entry's cacheCount (and therefore its unload bookkeeping) alone.
    // Note what is NOT done here: claiming ownership. The instance is resolvable
    // from cache, which is all `true` promises, but the entry may be the per-object
    // loader's — and {@link unseedDataSet} must not touch that one.
    if (dataSetCacheManager.isLoaded(key))
        return true;
    let dataSet;
    try {
        dataSet = dicomParser.parseDicom(bytes);
    }
    catch {
        return false;
    }
    // A per-object XHR already in flight for this same key can land after this
    // write and replace the entry with an equivalent dataset, double-counting the
    // bytes in the cache manager's private `cacheSizeInBytes`. That counter is
    // only ever surfaced through `getInfo()` and the `datasetscachechanged`
    // event, neither of which app-ui reads or subscribes to, and neither of which
    // gates eviction — cornerstone's image cache does that from its own
    // accounting. So the drift is inert: do not "fix" it by racing the loader.
    loadedDataSets[key] = { dataSet, cacheCount: 1 };
    seededKeys.add(key);
    return true;
}
/**
 * Load `cacheKey`'s dataset with `request` fetching the bytes, instead of the
 * loader's own XHR — or return null when there is nothing to start because the
 * dataset is already cached or already being fetched.
 *
 * WHY THROUGH THE CACHE MANAGER rather than fetching and then calling
 * {@link seedDataSet}: a seed only lands once the bytes are complete, and any
 * Cornerstone load of the same object that starts before then — the pane's own
 * `setStack`, a prefetch, a cine look-ahead — would see an empty cache and
 * start a second download of the whole clip. `dataSetCacheManager.load`
 * registers the request as THE in-flight load for the key at the moment it
 * starts, and `loadImage` joins an in-flight load for its key instead of
 * fetching (the `promises[uri]` branch of `load`). So from the first byte on,
 * every load of this object waits for these bytes. The parse and the cache
 * entry (and its byte accounting) are the manager's, exactly as for an XHR
 * load, so eviction and purge release it like any other dataset.
 *
 * The cache manager calls `request` synchronously when — and only when — it
 * starts a new load, which is how an already-in-flight load is detected: the
 * request never runs. Joining that load would leave a reference this caller
 * never releases, so the one `load` took is handed straight back.
 */
export function loadDataSetThrough(cacheKey, imageId, request) {
    if (!cacheKey || dataSetCacheManager.isLoaded(cacheKey))
        return null;
    let started = false;
    const load = dataSetCacheManager.load(cacheKey, (uri, id) => {
        started = true;
        return request(uri, id);
    }, imageId);
    if (!started) {
        load.cacheCount = (load.cacheCount ?? 1) - 1;
        return null;
    }
    return load;
}
/**
 * Give back the reference {@link loadDataSetThrough} holds on a settled load,
 * but only while some other load still holds one.
 *
 * Every Cornerstone load that joined the in-flight request took its own
 * reference and will release it when its image is decached; keeping ours on
 * top would pin the whole clip after the last of those images is gone. With no
 * other holder the reference is kept on purpose: the pane's own load may still
 * be queued behind others, and dropping the dataset now would make it fetch
 * the clip again. That remaining entry is the same shape a seeded one is, and
 * {@link releaseDataSet} / {@link purgeDataSetCache} free it the same way.
 */
export function releaseLoadReference(cacheKey) {
    const entry = loadedDataSets[cacheKey];
    if (entry && entry.cacheCount > 1)
        dataSetCacheManager.unload(cacheKey);
}
/**
 * Retract a dataset this module seeded — used when the bundle's trailer names
 * an ordinal as untrustworthy after its frame already looked healthy.
 *
 * A key this module did not write is left strictly alone. The trailer describes
 * the BUNDLE's copy of an object, and says nothing about a copy the per-object
 * loader fetched under the same key; unloading that one decrements a reference
 * we never took, which at cacheCount 1 deletes the dataset a viewport is
 * rendering from. Hence the ownership set rather than a bare `unload`.
 *
 * The removal itself goes through `unload` rather than deleting the map entry so
 * the cache manager's own accounting stays consistent.
 */
export function unseedDataSet(image) {
    const key = datasetCacheKey(image);
    if (!key)
        return;
    if (!seededKeys.delete(key))
        return;
    dataSetCacheManager.unload(key);
}
/**
 * Drop every cached dataset that no longer backs an image in Cornerstone's
 * image cache.
 *
 * Required, not hygiene. An image resolved from a seeded dataset takes
 * `loadImage`'s already-loaded fast path (`loadImageFromDataSet`), and that path
 * — unlike the fetch path — never installs a `decache` hook on the image load
 * object. Cornerstone's `cache.purgeCache()` therefore frees the decoded image
 * but leaves the underlying `DataSet` (the whole DICOM byte array) referenced by
 * the wadouri cache forever. Every viewer teardown and every cache trim has to
 * call this or a session that opens study after study climbs until the tab dies.
 *
 * The exception is load-bearing, not an optimisation. The wadouri metadata
 * provider answers Cornerstone's per-image lookups (`generalSeriesModule` and
 * friends) straight from the cached dataset, and `StackViewport.setStack` needs
 * those answers to turn a cached image into an actor. Drop the dataset while its
 * decoded image survives and the next `setStack` throws inside a
 * `Promise.allSettled`: nothing surfaces, no actor is built, and the pane sits
 * black under its placeholder for good. The worklist warm-up puts exactly such
 * images in the cache before the viewer mounts, and the viewer purges datasets
 * on the way in, so this has to leave the two caches consistent: an image with
 * its dataset, or neither.
 */
export function purgeDataSetCache() {
    const kept = new Set(Object.keys(loadedDataSets).filter(dataSetBacksACachedImage));
    if (kept.size === 0) {
        dataSetCacheManager.purge();
        seededKeys.clear();
        return;
    }
    for (const uri of Object.keys(loadedDataSets)) {
        if (kept.has(uri))
            continue;
        dropDataSet(uri);
    }
    for (const key of seededKeys) {
        if (!dataSetCacheManager.isLoaded(key))
            seededKeys.delete(key);
    }
}
/**
 * Drop one cached dataset outright, whoever loaded it and however many loads
 * share it.
 *
 * For the caller that has just evicted EVERY decoded image the dataset backs
 * (see ./imageCacheEviction): the same invariant as {@link purgeDataSetCache}
 * — an image with its dataset, or neither — applied to one object instead of
 * the whole cache.
 */
export function releaseDataSet(uri) {
    if (!dataSetCacheManager.isLoaded(uri))
        return;
    dropDataSet(uri);
    seededKeys.delete(uri);
}
function dropDataSet(uri) {
    // `unload` is reference-counted (one count per load that shared the entry)
    // and only frees at zero; collapse the count so one unload removes the
    // dataset with the manager's byte accounting kept right.
    loadedDataSets[uri].cacheCount = 1;
    dataSetCacheManager.unload(uri);
}
/**
 * Whether a dataset key still has a decoded (or in-flight) image in
 * Cornerstone's image cache: the single-frame id, or frame one of a multi-frame
 * object (the frame every first paint and every warm-up asks for).
 */
function dataSetBacksACachedImage(uri) {
    const frameOne = `${uri}${uri.includes('?') ? '&' : '?'}frame=1`;
    return [uri, frameOne].some((url) => cache.getImageLoadObject(`wadouri:${url}`) !== undefined);
}
/**
 * Whether `cacheKey`'s parsed dataset is already in the wadouri cache —
 * whoever put it there.
 *
 * Deliberately NOT scoped to {@link seededKeys}: the question this answers is
 * "would fetching these bytes be a second download?", and a dataset the
 * per-object loader's own XHR wrote answers that just as well as one a bundle
 * seeded. Ownership only matters for RETRACTION, which is why that set exists
 * and why this does not consult it.
 *
 * This is `seedDataSet`'s own already-cached predicate, exported so the
 * prefetcher's bundle transport can ask the same question one step earlier —
 * before spending a request rather than after parsing the response.
 */
export function isDataSetLoaded(cacheKey) {
    return dataSetCacheManager.isLoaded(cacheKey);
}
/** Number of datasets currently cached — for tests and diagnostics. */
export function cachedDataSetCount() {
    return dataSetCacheManager.getInfo().numberOfDataSetsCached;
}
/**
 * Total size, in bytes, of every dataset currently cached — both this
 * module's seeded entries and the per-object loader's own.
 *
 * This is what the prefetcher's dataset-cache high-water gate reads (see
 * `getDatasetCacheHighWaterBytes` in ./init): the wadouri cache has no
 * eviction of its own, so bundling has to watch this number itself rather
 * than rely on cornerstone's decoded-pixel headroom test, which cannot see
 * these bytes at all.
 */
export function cachedDataSetSizeBytes() {
    return dataSetCacheManager.getInfo().cacheSizeInBytes;
}
